"use client";

import React from "react";
import { CircleCheck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const auditTypes = [{
  id: "regulatory",
  title: "Audit Énergétique Réglementaire",
  badge: "Objectif : vente de votre bien",
  description: "Obligatoire pour la vente de maisons individuelles classées E, F ou G (passoires thermiques)",
  ctaText: "En savoir plus",
  ctaLink: "/ContactPage",
  isVoluntary: false
}, {
  id: "voluntary",
  title: "Audit Énergétique Volontaire",
  badge: "Objectif : réaliser des travaux de rénovation énergétique\xA0",
  description: "La première étape indispensable pour un projet de rénovation globale réussi et financé.",
  ctaText: "Demander un audit",
  ctaLink: "/ContactPage",
  isVoluntary: true
}];
const auditBenefits = [{
  auditId: "regulatory",
  text: "A l'initiative du vendeur, il conditionne la transaction immobilière."
}, {
  auditId: "regulatory",
  text: "Il informe l'acquéreur sur les travaux nécessaires pour améliorer la performance énergétique du bien et les aides mobilisables"
}, {
  auditId: "regulatory",
  text: "Valorisation possible de votre patrimoine. Estimation des coûts de travaux et des économies potentielles."
}, {
  auditId: "regulatory",
  text: "Il doit impérativement être réalisé par un auditeur certifié RGE."
}, {
  auditId: "voluntary",
  text: "Démarche proactive du propriétaire souhaitant effectuer des travaux de rénovation énergétique."
}, {
  auditId: "voluntary",
  text: "Volonté de réduire les consommations énergétiques et améliorer le confort des occupants."
}, {
  auditId: "voluntary",
  text: "Scénarios de travaux précis et adaptés à votre besoin."
}, {
  auditId: "voluntary",
  text: "Indispensable à l'obtention des aides, il doit également être réalisé par un auditeur certifié RGE."
}];
export default function EnergyAuditTypes() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="energyaudittypes" className="bg-background overflow-hidden ![padding:96px_0px_96px_0px]">
      <div className="container mx-auto px-6 max-w-6xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Les 2 types d'Audit Énergétique</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg !w-[900px]">2 cas de figure, 2 types d'audit mais le même livrable. Règlementaire ou volontaire, NSDB s'adapte à votre besoin et vous explique la différence en quelques mots.</p>
        </div>

        <div ref={ref} className={cn("grid grid-cols-1 lg:grid-cols-2 gap-8 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {auditTypes.map((audit, i) => {
          const isVoluntary = audit.isVoluntary;
          const cardBenefits = auditBenefits.filter(b => b.auditId === audit.id);
          return <div key={audit.id} data-index={i} className="h-full flex">
                <Card className={cn("flex flex-col w-full rounded-[12px] border-2 transition-transform duration-300 hover:-translate-y-1", isVoluntary ? "bg-primary text-primary-foreground border-secondary/30 shadow-xl shadow-primary/10" : "bg-card text-foreground border-secondary/40 shadow-lg shadow-secondary/5")}>
                  <CardHeader className="@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 ![padding:0px_24px_0px_24px]">
                    <div className="mb-4">
                      <Badge variant="outline" className={cn("font-mono text-xs px-3 py-1 border-transparent", isVoluntary ? "bg-primary-foreground/10 text-primary-foreground" : "bg-secondary/20 text-secondary-foreground")}>
                        {audit.badge}
                      </Badge>
                    </div>
                    <CardTitle className={cn("text-2xl md:text-3xl font-bold mb-2", !isVoluntary && "text-primary")}>
                      {audit.title}
                    </CardTitle>
                    <CardDescription className={cn("text-base", isVoluntary ? "text-primary-foreground/80" : "text-muted-foreground")}>
                      {audit.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-grow">
                    <ul className="space-y-4">
                      {cardBenefits.map((benefit, j) => <li key={j} data-index={j} className="flex items-start gap-3">
                          <CircleCheck className={cn("w-5 h-5 shrink-0 mt-0.5", isVoluntary ? "text-accent" : "text-primary")} />
                          <span className={cn("leading-tight", isVoluntary ? "text-primary-foreground/90" : "text-foreground/80")}>
                            {benefit.text}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="pt-6 mt-auto">
                    <Button asChild size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold text-base h-12 rounded-lg shadow-md">
                      <Link to={audit.ctaLink}>
                        {audit.ctaText}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}